import type { BulkSelectableRow } from '@/hooks/useBulkItemSelection';
import { formatMoney } from '@/utils/currency';

export type CatalogExportLayout = 'brochure' | 'priceSheet';

export interface CatalogExportOptions {
  companyName: string;
  layout: CatalogExportLayout;
  includeWholesale?: boolean;
  includeBarcode?: boolean;
  effectiveDate?: string;
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function groupByCategory(rows: BulkSelectableRow[]) {
  const map = new Map<string, BulkSelectableRow[]>();
  for (const row of rows) {
    const cat = row.categoryName || 'Other';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(row);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

function buildBrochureHtml(rows: BulkSelectableRow[], opts: CatalogExportOptions) {
  const date = opts.effectiveDate || new Date().toLocaleDateString();
  const groups = groupByCategory(rows);
  const cards = groups
    .map(([cat, items]) => {
      const itemCards = items
        .map((row) => {
          const img = row.item.image
            ? `<img src="${escapeHtml(row.item.image)}" alt="" style="width:100%;height:90px;object-fit:contain;border-radius:8px;background:#f9fafb;" />`
            : `<div style="width:100%;height:90px;background:#f3f4f6;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:11px;">No image</div>`;
          const uom = row.variant?.unit_of_measurement || row.item.unit_of_measurement || '';
          return `
            <div style="border:1px solid #e5e7eb;border-radius:12px;padding:10px;background:#fff;break-inside:avoid;">
              ${img}
              <p style="margin:8px 0 4px;font-size:12px;font-weight:600;color:#111827;line-height:1.3;min-height:32px;">${escapeHtml(row.label)}</p>
              <p style="margin:0;font-size:10px;color:#6b7280;">${escapeHtml(uom)}</p>
              <p style="margin:6px 0 0;font-size:16px;font-weight:700;color:#059669;">${escapeHtml(formatMoney(row.salePrice))}</p>
            </div>`;
        })
        .join('');
      return `
        <section style="margin-bottom:24px;">
          <h2 style="font-size:16px;font-weight:700;color:#374151;border-bottom:2px solid #059669;padding-bottom:6px;margin:0 0 12px;">${escapeHtml(cat)}</h2>
          <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;">${itemCards}</div>
        </section>`;
    })
    .join('');

  return `
    <div id="catalog-export-root" style="width:794px;padding:24px;font-family:system-ui,sans-serif;background:#fff;color:#111;">
      <header style="text-align:center;margin-bottom:20px;">
        <h1 style="margin:0;font-size:22px;font-weight:800;">${escapeHtml(opts.companyName)}</h1>
        <p style="margin:6px 0 0;font-size:12px;color:#6b7280;">Product catalog · ${escapeHtml(date)}</p>
      </header>
      ${cards}
      <footer style="margin-top:24px;text-align:center;font-size:10px;color:#9ca3af;">Prices effective ${escapeHtml(date)} — subject to change</footer>
    </div>`;
}

function buildPriceSheetHtml(rows: BulkSelectableRow[], opts: CatalogExportOptions) {
  const date = opts.effectiveDate || new Date().toLocaleDateString();
  const groups = groupByCategory(rows);
  let idx = 0;
  const tables = groups
    .map(([cat, items]) => {
      const trs = items
        .map((row) => {
          idx += 1;
          const uom = row.variant?.unit_of_measurement || row.item.unit_of_measurement || '';
          const bc = row.variant?.barcode || row.item.barcode || '';
          return `
            <tr style="border-bottom:1px solid #f3f4f6;">
              <td style="padding:6px 8px;font-size:11px;color:#6b7280;">${idx}</td>
              <td style="padding:6px 8px;font-size:12px;font-weight:500;">${escapeHtml(row.label)}</td>
              <td style="padding:6px 8px;font-size:11px;">${escapeHtml(uom)}</td>
              ${opts.includeBarcode !== false ? `<td style="padding:6px 8px;font-size:10px;color:#6b7280;">${escapeHtml(bc)}</td>` : ''}
              <td style="padding:6px 8px;font-size:12px;font-weight:600;text-align:right;">${escapeHtml(formatMoney(row.salePrice))}</td>
              ${opts.includeWholesale ? `<td style="padding:6px 8px;font-size:11px;text-align:right;color:#6b7280;">${escapeHtml(formatMoney(row.wholesalePrice))}</td>` : ''}
            </tr>`;
        })
        .join('');
      return `
        <section style="margin-bottom:20px;">
          <h2 style="font-size:14px;font-weight:700;color:#374151;margin:0 0 8px;">${escapeHtml(cat)}</h2>
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr style="background:#f9fafb;border-bottom:2px solid #e5e7eb;">
                <th style="padding:6px 8px;text-align:left;font-size:10px;color:#6b7280;">#</th>
                <th style="padding:6px 8px;text-align:left;font-size:10px;color:#6b7280;">Item</th>
                <th style="padding:6px 8px;text-align:left;font-size:10px;color:#6b7280;">UOM</th>
                ${opts.includeBarcode !== false ? '<th style="padding:6px 8px;text-align:left;font-size:10px;color:#6b7280;">Barcode</th>' : ''}
                <th style="padding:6px 8px;text-align:right;font-size:10px;color:#6b7280;">Retail</th>
                ${opts.includeWholesale ? '<th style="padding:6px 8px;text-align:right;font-size:10px;color:#6b7280;">Wholesale</th>' : ''}
              </tr>
            </thead>
            <tbody>${trs}</tbody>
          </table>
        </section>`;
    })
    .join('');

  return `
    <div id="catalog-export-root" style="width:794px;padding:24px;font-family:system-ui,sans-serif;background:#fff;color:#111;">
      <header style="margin-bottom:16px;">
        <h1 style="margin:0;font-size:20px;font-weight:800;">${escapeHtml(opts.companyName)}</h1>
        <p style="margin:4px 0 0;font-size:12px;color:#6b7280;">Daily price sheet · ${escapeHtml(date)}</p>
      </header>
      ${tables}
      <footer style="margin-top:16px;font-size:10px;color:#9ca3af;text-align:center;">Prices effective ${escapeHtml(date)}</footer>
    </div>`;
}

export async function exportCatalogPdf(rows: BulkSelectableRow[], opts: CatalogExportOptions) {
  if (rows.length === 0) throw new Error('No products selected');

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.innerHTML = opts.layout === 'brochure' ? buildBrochureHtml(rows, opts) : buildPriceSheetHtml(rows, opts);
  document.body.appendChild(container);

  try {
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');
    const root = container.querySelector('#catalog-export-root') as HTMLElement;
    const canvas = await html2canvas(root, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const suffix = opts.layout === 'brochure' ? 'brochure' : 'price-sheet';
    pdf.save(`${opts.companyName.replace(/\s+/g, '_')}_${suffix}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}

export function printCatalogHtml(rows: BulkSelectableRow[], opts: CatalogExportOptions) {
  const html = opts.layout === 'brochure' ? buildBrochureHtml(rows, opts) : buildPriceSheetHtml(rows, opts);
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!DOCTYPE html><html><head><title>Catalog</title></head><body>${html}</body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}
